import { useState } from 'react';
import { IoHome, IoCreate, IoCheckmarkCircle, IoPeople, IoLogoGoogle, IoEye, IoEyeOff } from 'react-icons/io5';
import { signInWithGooglePopup, signInWithEmailAndPassword } from '../../../utils/firebase.utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notifyBackendOfFirebaseAuth } from '../../../services/authService';
import { showAuthError } from '../../../utils/toast';

const HomeIcon = IoHome as React.ComponentType<{ className?: string }>;
const CreateIcon = IoCreate as React.ComponentType<{ className?: string }>;
const CheckmarkCircleIcon = IoCheckmarkCircle as React.ComponentType<{ className?: string }>;
const PeopleIcon = IoPeople as React.ComponentType<{ className?: string }>;
const LogoGoogleIcon = IoLogoGoogle as React.ComponentType<{ className?: string }>;

const features = [
  { icon: <HomeIcon className="text-orange-500" />, bg: 'bg-orange-100', label: 'Build Your College List' },
  { icon: <CreateIcon className="text-blue-500" />, bg: 'bg-blue-100', label: 'Instant Essay Feedback' },
  { icon: <CheckmarkCircleIcon className="text-green-500" />, bg: 'bg-green-100', label: 'SMS Application Updates' },
  { icon: <PeopleIcon className="text-purple-500" />, bg: 'bg-purple-100', label: 'Access Our Live Counselors' },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!email.includes('@') || !email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || isLoading) return;

    setIsLoading(true);
    try {
      const res = await signInWithEmailAndPassword(email, password);

      if (res.user) {
        const idToken = await res.user.getIdToken();
        const data = await notifyBackendOfFirebaseAuth(
          idToken,
          res.user.displayName || '',
          res.user.email || '',
          res.user.photoURL
        );

        updateUser({
          uid: res.user.uid,
          email: res.user.email || '',
          name: res.user.displayName || '',
          photoURL: res.user.photoURL || '',
          is_new_user: data.is_new_user,
          ...data.user,
        });
      }

      navigate('/');
    } catch (error: any) {
      if (
        error.code === 'auth/account-exists-with-different-credential' ||
        error.code === 'auth/invalid-credential'
      ) {
        // User may have signed up with Google — offer to set a password
        navigate(`/forgot-password?email=${encodeURIComponent(email)}&context=set-password`);
      } else if (error.code === 'auth/user-not-found') {
        showAuthError('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        showAuthError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        showAuthError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        showAuthError('Too many failed attempts. Please try again later.');
      } else {
        showAuthError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (isGoogleLoading) return;

    setIsGoogleLoading(true);
    try {
      const res = await signInWithGooglePopup();

      if (res.user) {
        const idToken = await res.user.getIdToken();
        const data = await notifyBackendOfFirebaseAuth(
          idToken,
          res.user.displayName || '',
          res.user.email || '',
          res.user.photoURL
        );

        updateUser({
          uid: res.user.uid,
          email: res.user.email || '',
          name: res.user.displayName || '',
          photoURL: res.user.photoURL || '',
          is_new_user: data.is_new_user,
          ...data.user,
        });
      }

      navigate('/');
    } catch (error) {
      showAuthError('Google login failed. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">
              <i className="fas fa-graduation-cap" />
            </span>
          </div>

          <h1 className="text-2xl font-bold text-center mb-2 font-inter">
            Thinking with you, not for you
          </h1>

          <p className="text-gray-600 text-center mb-6">
            Sign in to unlock powerful tools and features
          </p>

          <div className="space-y-4 mb-8">
            {features.map((f, i) => (
              <div key={i} className="flex items-center">
                <span className={`w-7 h-7 rounded-full ${f.bg} flex items-center justify-center mr-3`}>
                  {f.icon}
                </span>
                <span className="text-gray-700 text-base">{f.label}</span>
              </div>
            ))}
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: undefined });
                }}
                placeholder="Email address"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-transparent ${
                  errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: undefined });
                }}
                placeholder="Password"
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-transparent ${
                  errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
              </button>
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${
                isLoading
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
              } text-white font-bold text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500" style={{ background: 'var(--surface-primary)' }}>or</span>
            </div>
          </div>

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading}
            className={`w-full ${
              isGoogleLoading
                ? 'cursor-not-allowed opacity-60'
                : 'hover:bg-gray-50'
            } border border-gray-300 text-gray-700 font-medium text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all mb-6`}
            style={{ background: isGoogleLoading ? undefined : 'var(--surface-primary)' }}
          >
            {isGoogleLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogoGoogleIcon className="text-xl text-red-500" />
                <span>Continue with Google</span>
              </>
            )}
          </button>

          {/* Forgot Password */}
          <div className="text-center mb-4">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
              disabled={isLoading || isGoogleLoading}
            >
              Forgot Password?
            </button>
          </div>

          {/* Create Account */}
          <div className="text-center">
            <span className="text-gray-600 text-sm">Don't have an account? </span>
            <button
              onClick={() => navigate('/signup')}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
              disabled={isLoading || isGoogleLoading}
            >
              Create Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
